import { ReactNode, memo, useCallback, useMemo, useState } from 'react';
import { css } from '@emotion/react';
import { DemoChip } from '@frontend/analytics-shared';
import { LocationChip } from '@frontend/analytics-shared';
import { generateTrackingId, useCallIntelDefaultsShallowStore } from '@frontend/call-intel-core';
import { Chart, YAxisLabelValueTick } from '@frontend/charts';
import { useTranslation } from '@frontend/i18n';
import { formatters, useIsSmallMode } from '@frontend/shared';
import { useNavigate } from '@frontend/weave-router';
import { theme } from '@frontend/theme';
import { Text, Truncated } from '@frontend/design-system';
import { useActiveFilters } from '../hooks/use-active-filters';
import { useCallIntelDemoFlags } from '../hooks/use-call-intel-demo-flags';
import { useCallIntelLocations } from '../hooks/use-call-intel-locations';
import { useCallIntelShallowStore, type CallIntelSubViewType } from '../hooks/use-call-intel-store';

type Props = {
  categorisedComparison?: boolean;
  data?: Record<string, Record<string, number>>;
  internalPage?: boolean;
  isLoading?: boolean;
  title: string;
} & (
  | {
      colors?: never;
      dataType: Exclude<CallIntelSubViewType, 'follow-up'>;
      labels?: never;
    }
  | {
      colors: Record<string, string>;
      dataType?: never;
      labels: Record<string, ReactNode>;
    }
);

export const CallIntelLocationComparisonStackChart = memo(
  ({
    categorisedComparison,
    colors: propColors,
    data = {},
    dataType,
    internalPage,
    isLoading,
    labels: propLabels,
    title,
  }: Props) => {
    const { t } = useTranslation('analytics');
    const navigate = useNavigate();
    const { showDemoChipAndBanner } = useCallIntelDemoFlags();
    const { chartOutlineColors, dataColors, dataLabels } = useCallIntelDefaultsShallowStore(
      'chartOutlineColors',
      'dataColors',
      'dataLabels'
    );
    const { setSubView, subView } = useCallIntelShallowStore('setSubView', 'subView');
    const { filterHintText } = useActiveFilters();
    const { locations } = useCallIntelLocations();
    const { isSmallMode } = useIsSmallMode();
    const [isLargeChart, setIsLargeChart] = useState<boolean>(false);

    const chartProps = useMemo(() => {
      return {
        colors:
          propColors ||
          (dataType === 'appointment-type'
            ? dataColors.appointmentTypes
            : dataType === 'category'
            ? dataColors.categories
            : dataColors.sentiments),

        labels:
          dataType === 'appointment-type'
            ? dataLabels.appointmentTypes
            : dataType === 'category'
            ? dataLabels.categories
            : dataLabels.sentimentsWithEmoji,
        strokeColors:
          propColors ||
          (dataType === 'appointment-type'
            ? chartOutlineColors.appointmentTypes
            : dataType === 'category'
            ? chartOutlineColors.categories
            : chartOutlineColors.sentiments),
      };
    }, [
      chartOutlineColors.appointmentTypes,
      chartOutlineColors.categories,
      chartOutlineColors.sentiments,
      dataColors.appointmentTypes,
      dataColors.categories,
      dataColors.sentiments,
      dataLabels.appointmentTypes,
      dataLabels.categories,
      dataLabels.sentimentsWithEmoji,
      dataType,
      propColors,
    ]);

    const chartData = useMemo(() => {
      return {
        groups: Object.entries(data).map(([locationName, dataset]) => ({
          name: locationName,
          values: {
            ...dataset,
          },
        })),
      };
    }, [data]);

    const customTooltipTitle = useCallback(
      ({ groupName, hoveredSegment }: { groupName: string; hoveredSegment?: string | null }) => {
        return (
          <div>
            {hoveredSegment &&
              (propLabels ? (
                <div style={{ marginBottom: theme.spacing(1) }}>{propLabels[hoveredSegment]}</div>
              ) : (
                <Text color='subdued' size='small' style={{ marginBottom: theme.spacing(1) }}>
                  {internalPage &&
                    `${
                      {
                        ...dataLabels.appointmentTypes,
                        ...dataLabels.categories,
                        ...dataLabels.sentimentsWithEmoji,
                      }[subView?.id || '']
                    }: `}
                  {chartProps.labels?.[hoveredSegment]}
                </Text>
              ))}
            <LocationChip locationName={groupName} noTruncating />
          </div>
        );
      },
      [
        propLabels,
        internalPage,
        dataLabels.appointmentTypes,
        dataLabels.categories,
        dataLabels.sentimentsWithEmoji,
        subView?.id,
        chartProps.labels,
      ]
    );

    const handleBarClick = useCallback(
      ({ barId, group }: { barId: string; group: string }) => {
        const locationId = Object.entries(locations).find(([_key, value]) => value === group)?.[0];
        setSubView({
          id: barId,
          type: dataType,
        });
        navigate({
          search: {
            type: dataType,
            locationId,
          },
          to: '/analytics/call-intelligence/$id',
          params: {
            id: barId,
          },
        });
      },
      [locations, setSubView, dataType, navigate]
    );

    return (
      <Chart
        colors={chartProps.colors || {}}
        commonTooltipLabel={t('Calls')}
        isLoading={isLoading}
        labels={{
          ...chartProps.labels,
          groupNameHeader: t('Location'),
        }}
        strokeColors={chartProps.strokeColors}
      >
        <Chart.HeaderV2
          title={
            <Chart.HeaderV2.Title leftElement={showDemoChipAndBanner ? <DemoChip /> : null}>
              {title}
            </Chart.HeaderV2.Title>
          }
          subTitle={
            internalPage ? null : (
              <Text color='subdued' size='medium'>
                {categorisedComparison ? t('Click each bar to learn more.') : t('Click each segment to learn more.')}
              </Text>
            )
          }
          action={<Chart.HeaderV2.FilterText filterHintText={filterHintText} />}
        />

        {categorisedComparison ? (
          <Chart.CategoryBarChart
            appearance={{
              alternateCategoryBackgroundColor: theme.colors.neutral5,
              customCategoryNameRenderer:
                propLabels &&
                ((categoryId: string) => {
                  return categoryId === 'groupNameHeader' ? (
                    propLabels[categoryId]
                  ) : (
                    <div
                      css={css`
                        margin-bottom: ${theme.spacing(2)};
                      `}
                    >
                      {propLabels[categoryId]}
                    </div>
                  );
                }),
              customTooltipTitle,
              barSize: 16,
              ...(isSmallMode && {
                groupNameWidth: 100,
                groupNameRightMargin: 16,
                showSerials: false,
              }),
            }}
            data={data}
            {...(isSmallMode && {
              customGroupNameRenderer: ({ groupName }) => {
                return (
                  <Truncated as={Text} size='medium'>
                    {groupName}
                  </Truncated>
                );
              },
            })}
            onClick={internalPage ? undefined : handleBarClick}
            {...(!internalPage && {
              trackingIdBase: generateTrackingId({ component: `location-comparison-${dataType}-bar-chart` }),
            })}
          >
            {!isLoading && (
              <Text color='subdued' size='medium' textAlign='center' weight='bold'>
                {t('Count of Calls')}
              </Text>
            )}
          </Chart.CategoryBarChart>
        ) : (
          <>
            <Chart.Legends style={{ marginTop: theme.spacing(2) }} />
            <Chart.BarChart
              appearance={{
                customTooltipTitle,
                customYAxisTick: ({ labels, groupName, ...rest }) => {
                  return (
                    <YAxisLabelValueTick
                      {...rest}
                      label={labels?.[groupName] || groupName}
                      onChartSizeChange={(isLargeChart) => setIsLargeChart(isLargeChart)}
                      xTransitionLabel={-180}
                    />
                  );
                },
                groupsGap: 24,
                layout: 'vertical',
                margin: { top: 10, right: 20, left: isLargeChart ? 140 : 40 },
                maxHeight: 254,
                mode: 'stacked',
                showGridLines: true,
                showXAxis: true,
                showYAxis: true,
              }}
              data={chartData}
              onClick={internalPage ? undefined : handleBarClick}
              formatValue={formatters.value}
              {...(!internalPage && {
                baseTrackingId: generateTrackingId({ component: `location-comparison-${dataType}-bar-chart` }),
              })}
            />
            {!isLoading && (
              <Text color='subdued' size='medium' textAlign='center' weight='bold'>
                {t('Count of Calls')}
              </Text>
            )}
          </>
        )}
      </Chart>
    );
  }
);

CallIntelLocationComparisonStackChart.displayName = 'CallIntelLocationComparisonStackChart';
